import type {
  EventPayload,
  EventType,
  PixelConfig,
  SessionIds,
} from "./types.js";
import type { Transport } from "./transport.js";
import { sha256Hex } from "./util/sha256.js";

const PUSHSTATE_PATCHED = Symbol.for("@fieldswap/pixel:history-patched");
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type GetFp = () => string | undefined;

interface AutotrackContext {
  cfg: PixelConfig;
  ids: SessionIds;
  getFp: GetFp;
  transport: Transport;
}

function buildPayload(
  ctx: AutotrackContext,
  eventType: EventType,
  metadata: Record<string, unknown>,
  extra?: { email_hash?: string },
): EventPayload {
  const fp = ctx.getFp();
  const payload: EventPayload = {
    pixel_id: ctx.cfg.pixelId,
    session_id: ctx.ids.sessionId,
    visitor_id: ctx.ids.visitorId,
    event_type: eventType,
    page_url: typeof location !== "undefined" ? location.href : "",
    metadata,
    occurred_at: new Date().toISOString(),
  };
  if (fp) payload.device_fp = fp;
  if (extra?.email_hash) payload.email_hash = extra.email_hash;
  return payload;
}

export function installAutotrack(ctx: AutotrackContext): void {
  const FLAG = Symbol.for("@fieldswap/pixel:autotrack-installed");
  const w = window as unknown as Record<symbol, unknown>;
  if (w[FLAG]) return;
  w[FLAG] = true;

  const { transport, ids } = ctx;

  const emit = (
    eventType: EventType,
    metadata: Record<string, unknown>,
    opts?: { beacon?: boolean; extra?: { email_hash?: string } },
  ): void => {
    const payload = buildPayload(ctx, eventType, metadata, opts?.extra);
    transport.send(payload, opts?.beacon ? { beacon: true } : undefined);
    ids.touch();
  };

  // -- page_view ---------------------------------------------------------
  let crossedDepths = new Set<number>();
  const emitPageView = (): void => {
    crossedDepths = new Set<number>();
    emit("page_view", {
      referrer: document.referrer || "",
      title: document.title || "",
    });
  };
  emitPageView();

  // SPA route changes via history API (patch once).
  type PatchedHistory = History & { [PUSHSTATE_PATCHED]?: boolean };
  const hist = window.history as PatchedHistory;
  if (!hist[PUSHSTATE_PATCHED]) {
    const origPush = hist.pushState.bind(hist);
    const origReplace = hist.replaceState.bind(hist);
    hist.pushState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      origPush(data, unused, url ?? null);
      window.dispatchEvent(new Event("fs:locationchange"));
    } as History["pushState"];
    hist.replaceState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      origReplace(data, unused, url ?? null);
      window.dispatchEvent(new Event("fs:locationchange"));
    } as History["replaceState"];
    window.addEventListener("popstate", () => {
      window.dispatchEvent(new Event("fs:locationchange"));
    });
    window.addEventListener("fs:locationchange", () => {
      emitPageView();
    });
    hist[PUSHSTATE_PATCHED] = true;
  }

  // -- scroll_depth ------------------------------------------------------
  const thresholds = [25, 50, 75, 100] as const;
  let scrollScheduled = false;
  const onScroll = (): void => {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(() => {
      scrollScheduled = false;
      const doc = document.documentElement;
      const body = document.body;
      const docHeight = Math.max(
        doc.scrollHeight,
        body ? body.scrollHeight : 0,
        doc.offsetHeight,
        body ? body.offsetHeight : 0,
      );
      const viewport = window.innerHeight;
      if (docHeight <= 0 || viewport <= 0) return;
      const pct = Math.min(
        100,
        Math.round(((window.scrollY + viewport) / docHeight) * 100),
      );
      for (const t of thresholds) {
        if (pct >= t && !crossedDepths.has(t)) {
          crossedDepths.add(t);
          emit("scroll_depth", { depth: t });
        }
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // -- session_end -------------------------------------------------------
  let sessionEndSent = false;
  const sendSessionEnd = (): void => {
    if (sessionEndSent) return;
    sessionEndSent = true;
    emit("session_end", {}, { beacon: true });
  };
  window.addEventListener("beforeunload", sendSessionEnd);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendSessionEnd();
  });

  // -- email blur capture ------------------------------------------------
  document.addEventListener(
    "blur",
    (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== "email") return;
      const raw = target.value.trim().toLowerCase();
      if (!raw || !EMAIL_RE.test(raw)) return;
      void (async (): Promise<void> => {
        try {
          const hash = await sha256Hex(raw);
          emit(
            "form_start",
            { field: target.name || "email" },
            { extra: { email_hash: hash } },
          );
        } catch {
          // ignore — never block customer page
        }
      })();
    },
    true,
  );
}
